<template>
	<Teleport to="body">
		<div
			v-if="activeMenu"
			ref="menuRef"
			:class="bemm('', [resolvedDirection, resolvedAlignment])"
			:style="menuStyle"
			@click.stop
			@contextmenu.prevent
		>
			<!-- Wrapped rather than classed directly: beforeComponent is supplied by
			the caller and may render a fragment, which cannot carry a class. -->
			<div v-if="activeMenu.beforeComponent" :class="bemm('before')">
				<component :is="activeMenu.beforeComponent" />
			</div>
			<ul :class="bemm('list')">
				<li
					v-for="(item, index) in resolvedItems"
					:key="resolveItemKey(item, index)"
					:class="bemm('list-item')"
				>
					<hr v-if="item.type === 'divider'" :class="bemm('divider')" />
					<Button
						v-else
						:class="
							bemm('item', {
								disabled: item.disabled,
								locked: item.locked,
								selected: item.selected,
							})
						"
						:variant="ButtonVariant.GHOST"
						:icon="item.icon"
						:color="resolveItemColor(item)"
						:status="item.status || null"
						:full-width="true"
						:disabled="item.disabled"
						:locked="item.locked"
						:tooltip="item.tooltip || null"
						@click="handleSelect(item)"
					>
						<span :class="bemm('label')">{{ item.label }}</span>
					</Button>
				</li>
			</ul>
		</div>
	</Teleport>
</template>

<script lang="ts" setup>
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from 'vue';
import { useBemm } from 'bemm';
import { Button, ButtonVariant } from '../Button';
import { Color } from '@arritech/shared/types';
import { contextMenuService } from './ContextMenu.service';
import type { ContextMenuInstance } from './ContextMenu.model';
import type { DropdownItem, DropdownOption } from '../Dropdown';

const VIEWPORT_PADDING = 12;

const bemm = useBemm('ar-context-menu-host', {
	return: 'string',
	includeBaseClass: true,
});

const menuRef = ref<HTMLElement | null>(null);
const coordinates = ref({ left: VIEWPORT_PADDING, top: VIEWPORT_PADDING });
const activeMenu = computed<ContextMenuInstance | null>(
	() => contextMenuService.menu.value
);
const resolvedItems = computed<DropdownOption[]>(() => {
	const items = activeMenu.value?.items || [];
	const nonDeleteItems = items.filter((item) => !isDeleteItem(item));
	const deleteItems = items.filter(isDeleteItem);

	return [...nonDeleteItems, ...deleteItems];
});
const resolvedAlignment = computed(() => activeMenu.value?.align || 'left');
const resolvedDirection = computed(() =>
	activeMenu.value?.openUp ? 'up' : 'down'
);

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);

const updatePosition = () => {
	if (!activeMenu.value || !menuRef.value || typeof window === 'undefined') {
		return;
	}

	const rect = menuRef.value.getBoundingClientRect();
	const rawLeft =
		resolvedAlignment.value === 'right'
			? activeMenu.value.x - rect.width
			: activeMenu.value.x;
	const rawTop = activeMenu.value.openUp
		? activeMenu.value.y - rect.height
		: activeMenu.value.y;

	coordinates.value = {
		left: clamp(
			rawLeft,
			VIEWPORT_PADDING,
			window.innerWidth - rect.width - VIEWPORT_PADDING
		),
		top: clamp(
			rawTop,
			VIEWPORT_PADDING,
			window.innerHeight - rect.height - VIEWPORT_PADDING
		),
	};
};

const schedulePositionUpdate = () => {
	nextTick(() => {
		updatePosition();
	});
};

const menuStyle = computed<Record<string, string>>(() => {
	if (!activeMenu.value) {
		return {};
	}

	return {
		position: activeMenu.value.position || 'fixed',
		left: `${coordinates.value.left}px`,
		top: `${coordinates.value.top}px`,
		minWidth: `${activeMenu.value.minWidth || 220}px`,
	};
});

const resolveItemKey = (item: DropdownOption, index: number) => {
	if (item.type === 'divider') {
		return `divider-${index}`;
	}

	return String(item.key || item.value || item.label || index);
};

const isDeleteItem = (item: DropdownOption) => {
	if (item.type === 'divider') {
		return false;
	}

	const icon = String(item.icon || '').toLowerCase();
	const value = String(item.value || '').toLowerCase();
	const key = String(item.key || '').toLowerCase();

	return (
		icon.includes('trash') ||
		icon.includes('delete') ||
		value.includes('delete') ||
		key.includes('delete')
	);
};

const resolveItemColor = (item: DropdownItem) => {
	if (item.color) {
		return item.color;
	}

	if (isDeleteItem(item)) {
		return Color.ERROR;
	}

	return Color.FOREGROUND;
};

const handleSelect = (item: DropdownItem) => {
	contextMenuService.select(item);
};

const handlePointerDown = (event: Event) => {
	if (!activeMenu.value) {
		return;
	}

	const target = event.target as Node | null;
	if (target && menuRef.value?.contains(target)) {
		return;
	}

	contextMenuService.close(activeMenu.value.id);
};

const handleViewportChange = () => {
	if (!activeMenu.value) {
		return;
	}

	schedulePositionUpdate();
};

const handleKeydown = (event: KeyboardEvent) => {
	if (event.key !== 'Escape' || !activeMenu.value) {
		return;
	}

	contextMenuService.close(activeMenu.value.id);
};

onMounted(() => {
	document.addEventListener('pointerdown', handlePointerDown, true);
	document.addEventListener('scroll', handleViewportChange, true);
	window.addEventListener('resize', handleViewportChange);
	window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
	document.removeEventListener('pointerdown', handlePointerDown, true);
	document.removeEventListener('scroll', handleViewportChange, true);
	window.removeEventListener('resize', handleViewportChange);
	window.removeEventListener('keydown', handleKeydown);
	contextMenuService.clear();
});

watch(activeMenu, () => {
	schedulePositionUpdate();
});
</script>

<style lang="scss">
.ar-context-menu-host {
	$b: &;
	z-index: 1000;
	background: var(--color-background);
	border: 1px solid color-mix(in srgb, var(--color-foreground), transparent 88%);
	border-radius: var(--border-radius-l);
	box-shadow:
		0 1rem 2rem color-mix(in srgb, var(--color-foreground), transparent 88%),
		0 0 0 1px color-mix(in srgb, var(--color-background), transparent 15%) inset;
	padding: var(--space-xs);

	&__before {
		margin-bottom: var(--space-xs);
	}

	&__list {
		display: flex;
		flex-direction: column;
		gap: var(--space-xs);
		margin: 0;
		padding: 0;
		list-style: none;
	}

	&__list-item {
		margin: 0;
	}

	&__divider {
		border: 0;
		border-top: 1px solid
			color-mix(in srgb, var(--color-foreground), transparent 88%);
		margin: var(--space-xs) 0;
	}

	&__item {
		width: 100%;
		--button-border-radius: var(--border-radius);
		--button-padding: var(--space-s) var(--space-m);
		--button-text: var(--color-foreground);
		--button-icon-color: currentColor;
		--button-background-color: transparent;
		--button-background-color--hover: color-mix(
			in srgb,
			var(--color-foreground),
			transparent 94%
		);

		.ar-button__container {
			justify-content: flex-start;
		}
	}

	&__item--selected {
		--button-background-color: color-mix(
			in srgb,
			var(--color-foreground),
			transparent 92%
		);
	}

	&__label {
		min-width: 0;
		white-space: nowrap;
	}
}
</style>
